import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import s from './StepWizard.module.scss';

// A replacement for the part of react-step-wizard 5.3.11 the app uses (lazy mount, no transitions, no hash
// navigation). Same markup: a div with the nav (cloned with the wizard props) and a div holding only the active
// step, wrapped in its own div and cloned with the wizard props. The step wrapper has a key per step, so a step
// change mounts the new step afresh (its Formik form starts from the initial values again).

/** The props the wizard adds to the nav and to the active step. The steps are numbered from 1. */
export interface IStepWizardChildProps {
  currentStep: number;
  totalSteps: number;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  firstStep: () => void;
  lastStep: () => void;
}

export interface IStepWizardProps {
  /** The step to start on, read once on mount; the first step when it is missing, null or not a step. */
  initialStep?: number | null;
  nav?: ReactElement<Partial<IStepWizardChildProps>>;
  children: ReactNode;
}

// As react-step-wizard: `initialStep - 1` when it is a step other than the first, the first step otherwise.
const getInitialIndex = (initialStep: number | null | undefined, totalSteps: number) => {
  const index = (initialStep ?? 0) - 1;
  if (Number.isInteger(index) && index > 0 && index < totalSteps) return index;
  return 0;
};

interface IWithWizardPropsProps {
  element: ReactNode;
  wizardProps: IStepWizardChildProps;
}

// Clones an element of a component (function, memo, forwardRef) with the wizard props; DOM elements and texts are
// rendered as they are (react-step-wizard did that for the steps; the app's navs are components). A component of its own (no markup) because the navigation functions read refs, which a
// render may not do: as props they are only called from event handlers and effects.
const WithWizardProps = ({ element, wizardProps }: IWithWizardPropsProps) => {
  if (!isValidElement<Partial<IStepWizardChildProps>>(element)) return element;
  if (typeof element.type !== 'function' && typeof element.type !== 'object') return element;
  return cloneElement(element, wizardProps);
};

const StepWizard = ({ initialStep, nav, children }: IStepWizardProps) => {
  const steps = Children.toArray(children);
  const totalSteps = steps.length;
  const [activeIndex, setActiveIndex] = useState(() => getInitialIndex(initialStep, totalSteps));

  // The navigation functions keep their identity; like the class component of react-step-wizard they read the
  // committed step, so two calls in one event handler both start from the same step.
  const committed = useRef({ activeIndex, totalSteps });
  useLayoutEffect(() => {
    committed.current = { activeIndex, totalSteps };
  });

  const setActiveStep = useCallback((index: number) => {
    const { current } = committed;
    if (index === current.activeIndex) return;
    if (!Number.isInteger(index) || index < 0 || index >= current.totalSteps) {
      // eslint-disable-next-line no-console -- the development warning of react-step-wizard
      if (import.meta.env.DEV) console.error(`${index + 1} is an invalid step`);
      return;
    }
    setActiveIndex(index);
  }, []);

  const goToStep = useCallback((step: number) => setActiveStep(step - 1), [setActiveStep]);
  const nextStep = useCallback(
    () => setActiveStep(committed.current.activeIndex + 1),
    [setActiveStep],
  );
  const previousStep = useCallback(
    () => setActiveStep(committed.current.activeIndex - 1),
    [setActiveStep],
  );
  const firstStep = useCallback(() => goToStep(1), [goToStep]);
  const lastStep = useCallback(() => goToStep(committed.current.totalSteps), [goToStep]);

  const wizardProps: IStepWizardChildProps = {
    currentStep: activeIndex + 1,
    totalSteps,
    nextStep,
    previousStep,
    goToStep,
    firstStep,
    lastStep,
  };

  const activeStep = steps[activeIndex];

  return (
    <div>
      {nav ? <WithWizardProps element={nav} wizardProps={wizardProps} /> : null}
      <div className={s.container}>
        {activeStep ? (
          <div key={activeIndex} className={s.step}>
            <WithWizardProps element={activeStep} wizardProps={wizardProps} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StepWizard;
